import { Link } from 'react-router-dom';
import { useSEO } from '../../../utils/seo';

export default function RanchoCordovaAreaPage() {
  useSEO({
    title: 'Solar Panel Cleaning & Bird Proofing Rancho Cordova CA - Window Washing | Mr. EcoClean',
    description: 'Expert solar panel cleaning, window washing, and bird proofing in Rancho Cordova CA. Maximize solar efficiency, crystal clear windows. Serving all Rancho Cordova areas. Call (916) 836-6145 for free quote.',
    keywords: 'solar panel cleaning Rancho Cordova, window washing Rancho Cordova, bird proofing Rancho Cordova',
    canonical: '/areas/rancho-cordova',
    ogType: 'website',
    schema: {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Solar Panel Cleaning and Bird Proofing",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Mr. EcoClean",
        "telephone": "+19168366145",
        "email": "Jason@mrecoclean.com"
      },
      "areaServed": {
        "@type": "City",
        "name": "Rancho Cordova",
        "containedInPlace": {
          "@type": "State",
          "name": "California"
        }
      }
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white shadow-sm">
        <nav className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="cursor-pointer">
              <h1 className="text-2xl font-bold text-red-600" style={{ fontFamily: '"Pacifico", serif' }}>Mr. EcoClean</h1>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">Home</Link>
              <Link to="/services" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">Services</Link>
              <Link to="/areas-covered" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">Areas Covered</Link>
              <Link to="/about" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">About</Link>
              <Link to="/contact" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">Contact</Link>
            </div>
          </div>
        </nav>
      </header>

      <section className="relative bg-cover bg-center bg-no-repeat py-32" style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/3fc10050f49918f85c23615e2bd895ca.jpg')` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">Solar Panel Cleaning & Bird Proofing in Rancho Cordova, CA</h2>
          <p className="text-xl text-white mb-8 max-w-3xl mx-auto">Professional solar panel cleaning, window washing, and bird proofing for Rancho Cordova homes and businesses. Serving all Rancho Cordova communities with eco-friendly methods.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-red-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer">Get Free Quote</Link>
            <a href="tel:916-836-6145" className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Call (916) 836-6145</a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-4xl font-bold text-gray-900 mb-12 text-center">Our Rancho Cordova Services</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-sun-line text-2xl text-sky-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Solar Panel Cleaning</h4>
              <p className="text-gray-600 mb-6">Professional solar panel cleaning for Rancho Cordova homes using deionized water. Remove dust, pollen, and debris that reduce energy production by up to 25%. Our gentle cleaning method protects your panels while maximizing output and extending their lifespan for years to come.</p>
              <Link to="/contact" className="text-sky-600 font-semibold hover:text-sky-700 cursor-pointer">Get Quote →</Link>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-window-line text-2xl text-sky-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Window Washing</h4>
              <p className="text-gray-600 mb-6">Crystal clear windows for Rancho Cordova homes and businesses. Our professional window washing delivers streak-free results every time. We clean interior and exterior windows, screens, tracks, and sills. Enjoy brighter rooms, enhanced curb appeal, and spotless windows that let in maximum natural light.</p>
              <Link to="/contact" className="text-sky-600 font-semibold hover:text-sky-700 cursor-pointer">Get Quote →</Link>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-sky-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Solar Bird Proofing</h4>
              <p className="text-gray-600 mb-6">Protect your solar investment from bird damage in Rancho Cordova. Birds nesting under solar panels cause damaged wiring, fire hazards, and costly repairs. Our professional mesh installation creates a humane barrier that prevents nesting while allowing proper airflow. Includes thorough cleanup of existing nests and droppings.</p>
              <Link to="/contact" className="text-sky-600 font-semibold hover:text-sky-700 cursor-pointer">Get Quote →</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-4xl font-bold text-gray-900 mb-12 text-center">Benefits of Regular Solar Panel Cleaning in Rancho Cordova</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-flashlight-line text-2xl text-sky-600"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-2">Increase Efficiency</h5>
              <p className="text-gray-600">Clean panels produce up to 25% more energy than dirty ones</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-2xl text-sky-600"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-2">Save Money</h5>
              <p className="text-gray-600">Lower energy bills and maximize your solar ROI</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-2xl text-sky-600"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-2">Extend Lifespan</h5>
              <p className="text-gray-600">Regular cleaning prevents damage and extends panel life</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-star-line text-2xl text-sky-600"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-2">Protect Warranty</h5>
              <p className="text-gray-600">Maintain manufacturer warranty requirements</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-red-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-4xl font-bold text-white mb-6">Ready to Clean Your Rancho Cordova Solar Panels?</h3>
          <p className="text-xl text-red-100 mb-8">Get a free quote for solar panel cleaning, window washing, or bird proofing in Rancho Cordova today.</p>
          <a href="tel:916-836-6145" className="bg-white text-red-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-block">Call: (916) 836-6145</a>
        </div>
      </section>

      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h4 className="text-2xl font-bold text-sky-400 mb-4" style={{ fontFamily: '"Pacifico", serif' }}>Mr. EcoClean</h4>
              <p className="text-gray-300 mb-4">Professional solar panel cleaning, window washing, and bird proofing services for Rancho Cordova and surrounding areas.</p>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Services</h5>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/services" className="hover:text-sky-400 cursor-pointer">Solar Panel Cleaning</Link></li>
                <li><Link to="/services" className="hover:text-sky-400 cursor-pointer">Window Washing</Link></li>
                <li><Link to="/services" className="hover:text-sky-400 cursor-pointer">Solar Bird Proofing</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Contact Info</h5>
              <div className="space-y-2 text-gray-300">
                <p><a href="tel:916-836-6145" className="hover:text-sky-400 cursor-pointer">(916) 836-6145</a></p>
                <p><a href="mailto:Jason@mrecoclean.com" className="hover:text-sky-400 cursor-pointer">Jason@mrecoclean.com</a></p>
              </div>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Business Hours</h5>
              <div className="space-y-2 text-gray-300">
                <p>Monday - Friday: 7:00 AM - 6:00 PM</p>
                <p>Saturday: 8:00 AM - 4:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Mr. EcoClean. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-sky-400 cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
